import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import PropTypes from 'prop-types';

/**
 * Styled notice dialog shown when a user who reached APWorks through the
 * Nexelus/ERP portal attempts to log out, clarifying that only the APWorks
 * session will end and the Nexelus session remains active.
 */
export function ErpLogoutNoticeDialog({ open, onClose, onConfirm, onCancel }) {
    const handleCancel = () => {
        if (onCancel) onCancel();
        if (onClose) onClose();
    };

    const handleConfirm = () => {
        if (onConfirm) onConfirm();
        if (onClose) onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleCancel}
            aria-labelledby="erp-logout-dialog-title"
            aria-describedby="erp-logout-dialog-description"
            maxWidth="xs"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    overflow: 'hidden',
                },
            }}
        >
            <Box
                sx={{
                    background: 'linear-gradient(135deg, #1e88e5 0%, #1565c0 100%)',
                    pt: 3,
                    pb: 2.5,
                    px: 3,
                    position: 'relative',
                    textAlign: 'center',
                }}
            >
                <IconButton
                    onClick={handleCancel}
                    size="small"
                    aria-label="close"
                    sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        color: 'rgba(255,255,255,0.85)',
                        '&:hover': { backgroundColor: 'rgba(255,255,255,0.15)' },
                    }}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
                <Box
                    sx={{
                        width: 56,
                        height: 56,
                        borderRadius: '50%',
                        backgroundColor: 'rgba(255,255,255,0.18)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 'auto',
                        mb: 1.5,
                    }}
                >
                    <InfoOutlinedIcon sx={{ color: '#fff', fontSize: 30 }} />
                </Box>
                <Typography
                    id="erp-logout-dialog-title"
                    variant="h6"
                    sx={{ color: '#fff', fontWeight: 600, letterSpacing: 0.2 }}
                >
                    Logout
                </Typography>
            </Box>

            <DialogContent sx={{ px: 3, pt: 3, pb: 1 }}>
                <Typography
                    id="erp-logout-dialog-description"
                    variant="body1"
                    sx={{ color: 'text.secondary', textAlign: 'center', lineHeight: 1.6 }}
                >
                    You are logged in through <strong>Nexelus</strong>. Logging out here will only
                    end your APWorks session; you will remain signed in to Nexelus.
                </Typography>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 3, pt: 2, justifyContent: 'center', gap: 1.5 }}>
                <Button
                    onClick={handleCancel}
                    variant="outlined"
                    sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        minWidth: 110,
                        fontWeight: 500,
                    }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleConfirm}
                    variant="contained"
                    autoFocus
                    startIcon={<LogoutIcon fontSize="small" />}
                    sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        minWidth: 110,
                        fontWeight: 500,
                        boxShadow: 'none',
                        background: 'linear-gradient(135deg, #1e88e5 0%, #1565c0 100%)',
                        '&:hover': {
                            background: 'linear-gradient(135deg, #1976d2 0%, #0d47a1 100%)',
                            boxShadow: 'none',
                        },
                    }}
                >
                    Logout
                </Button>
            </DialogActions>
        </Dialog>
    );
}

ErpLogoutNoticeDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func,
    onConfirm: PropTypes.func,
    onCancel: PropTypes.func,
};
